import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { useSite } from "@/lib/site";
import { useTheme } from "@/lib/theme";

export default function Navbar() {
  const { settings, nav, blogCategories = [] } = useSite();
  const { resolved } = useTheme();
  const [open, setOpen] = useState(false);
  const [blogOpen, setBlogOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); setBlogOpen(false); }, [loc.pathname]);

  const logo = resolved === "dark" ? settings.logo_light : settings.logo_dark;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background"
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex min-h-11 items-center" data-testid="nav-logo">
          {logo ? <img src={logo} alt={settings.brand_name || "Digitalis"} className="h-9 md:h-11 w-auto" /> : <span className="font-display font-extrabold text-2xl">Digitalis.</span>}
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
          {nav.map((n) => {
            const isBlog = n.url === "/blog";
            const active = isBlog ? loc.pathname.startsWith("/blog") : loc.pathname === n.url;
            if (isBlog) return (
              <div key={n.url} className="group relative">
                <div className={`relative flex items-center rounded-full ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Link to={n.url} className="py-2 pl-4 pr-1 text-[15px] font-medium">{n.label}</Link>
                  <button type="button" onClick={() => setBlogOpen((value) => !value)} className="flex h-9 w-9 items-center justify-center" aria-label="Buka kategori blog" aria-expanded={blogOpen}>
                    <ChevronDown className={`h-4 w-4 transition-transform ${blogOpen ? "rotate-180" : ""}`} />
                  </button>
                  {active && <span className="absolute bottom-0 left-4 right-4 h-[2px] rounded-full bg-primary" />}
                </div>
                <div className={`absolute left-1/2 top-full w-64 -translate-x-1/2 pt-3 transition-all ${blogOpen ? "visible translate-y-0 opacity-100" : "invisible -translate-y-1 opacity-0 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"}`}>
                  <div className="rounded-2xl border border-border bg-card p-2 soft-shadow">
                    <Link to="/blog" className="block rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-secondary">Semua Artikel</Link>
                    {blogCategories.map((category) => <Link key={category.slug} to={`/blog/category/${category.slug}`} className="block rounded-xl px-3 py-2.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">{category.name}</Link>)}
                  </div>
                </div>
              </div>
            );
            return (
              <Link
                key={n.url}
                to={n.url}
                target={n.new_tab ? "_blank" : undefined}
                className={`relative px-4 py-2 rounded-full text-[15px] font-medium transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
              >
                {n.label}
                {active && <span className="absolute left-4 right-4 -bottom-0.5 h-[2px] rounded-full bg-primary" />}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><ThemeToggle /></div>
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-1.5 btn-primary px-5 py-2.5 rounded-full text-sm font-semibold group hover:opacity-90 hover:-translate-y-0.5 transition-all soft-shadow"
            data-testid="nav-cta"
          >
            Mulai Project
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
          <button className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="nav-mobile-menu">
          <nav className="flex flex-col px-5 py-6 gap-1">
            {nav.map((n) => n.url === "/blog" ? (
              <div key={n.url} className="border-y border-border/60 py-1">
                <div className="flex items-center justify-between">
                  <Link to="/blog" className="py-3 font-display text-2xl font-bold" data-testid="nav-mobile-blog">{n.label}</Link>
                  <button type="button" onClick={() => setBlogOpen((value) => !value)} className="flex h-11 w-11 items-center justify-center" aria-label="Buka kategori blog" aria-expanded={blogOpen}><ChevronDown className={`h-5 w-5 transition-transform ${blogOpen ? "rotate-180" : ""}`} /></button>
                </div>
                {blogOpen && <div className="grid grid-cols-2 gap-2 pb-3"><Link to="/blog" className="filter-chip justify-center">Semua Artikel</Link>{blogCategories.map((category) => <Link key={category.slug} to={`/blog/category/${category.slug}`} className="filter-chip justify-center text-center">{category.name}</Link>)}</div>}
              </div>
            ) : (
              <Link key={n.url} to={n.url} className="py-3 font-display text-2xl font-bold" data-testid={`nav-mobile-${n.label.toLowerCase()}`}>{n.label}</Link>
            ))}
            <div className="mt-4 flex items-center justify-between">
              <ThemeToggle />
              <Link to="/contact" className="btn-primary rounded-full px-5 py-3 text-sm font-semibold">Mulai Project</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
